import { Injectable } from '@angular/core';
import { EVM, Route, RoutesRequest, RoutesResponse, Token, createConfig, getRoutes, getToken, getTokenBalances, getTokens } from '@lifi/sdk';
import { CryptoWalletService } from '@services/crypto-wallet/crypto-wallet.service';
import { sleep } from '@services/utils';
import { Wallet } from '@services/wallets/wallet.service';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { blockchainsConnectionInfo } from '@common/blockchains/blockchains';
import { ethers } from 'ethers';
import { IToken } from './utils';

const getChainRpcs = () =>
{
    const chainMap: Record<number, string[]> = {};

    Object.keys(blockchainsConnectionInfo).map(chainId => chainMap[ chainId ] = blockchainsConnectionInfo[ chainId ].rpc);

    return chainMap;
};

@Injectable({
    providedIn: 'root'
})
export class LifiService
{
    private lifiSdk = createConfig({
        integrator: 'cw-dev',
        providers: [ EVM() ]
    });

    private sdkTokens: { [ chainId: number ]: Token[]; };
    private supportedChains: number[];
    private userWallet: Wallet;

    private initState$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

    constructor(
        private cryptoWalletService: CryptoWalletService,
    )
    {
        this.init();
    }

    public async swap(route: Route): Promise<string>
    {
        const txHash = await this.userWallet.swap(route);
        return txHash;
    }

    public async isSwapSupported(chainId: number): Promise<boolean>
    {
        await this.waitInit();
        return this.supportedChains.includes(chainId);
    }

    public async setWallet(walletAddress: string, cwId: string)
    {
        await this.waitInit();
        this.userWallet = await this.cryptoWalletService.getWalletByAddress(cwId, walletAddress);
    }

    public async getTokenWithUsersCurrencies(chainId: number): Promise<IToken[]>
    {
        await this.waitInit();
        const currencies = await this.cryptoWalletService
            .getCurrenciesByWalletID(
                this.userWallet.getCryptoWalletId(),
                this.userWallet.getId().toString()
            );

        const addresses = currencies
            .filter(currency => currency.getChainId() === chainId)
            .map(currency => currency.getTokenAddress() ? currency.getTokenAddress() : ethers.ZeroAddress.toLowerCase());

        const sdkTokens = this.sdkTokens[ chainId ];

        await Promise.allSettled(addresses.map(async (address) =>
        {
            if (sdkTokens.some(token => token.address.toLowerCase() === address.toLowerCase()))
            {
                return;
            }
            const token = await getToken(chainId, address);
            sdkTokens.push(token);
        }));

        const balances: any = await getTokenBalances(this.userWallet.getAddress(), sdkTokens);

        return balances.map(token =>
        {
            return {
                ...token,
                amount: Number(
                    ethers.formatUnits(
                        token.amount,
                        token.decimals || 18
                    )
                ),
                swapAmount: null
            };
        });
    }

    private async init()
    {
        this.sdkTokens = (await getTokens()).tokens;
        this.supportedChains = Object.keys(this.sdkTokens).map(chainId => Number(chainId));
        this.initState$.next(true);
    }

    private async waitInit()
    {
        const initState = await this.initState$.pipe(take(1)).toPromise();
        if (!initState)
        {
            sleep(500);
            await this.waitInit();
        }
    }

    public async calculateAmount(request: RoutesRequest): Promise<RoutesResponse>
    {
        const result = await getRoutes(request);
        return result;
    }
}
